import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

const BusinessDetailPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State variables
  const [business, setBusiness] = useState(null);
  const [businessJobs, setBusinessJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [businessImage, setBusinessImage] = useState(null);
  
  // Fetch business and jobs data
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      try {
        setLoading(true);
        console.log(`Fetching business details for ID: ${businessId}`);
        
        // Get business data
        const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
        
        if (!businessDoc.exists()) {
          console.log('Business not found');
          setLoading(false);
          return;
        }
        
        const businessData = businessDoc.data();
        console.log('Business data:', businessData);
        
        // Get business logo and image from storage if available
        if (businessData.businessImage && businessData.businessImage.path) {
          try {
            const imageRef = ref(storage, businessData.businessImage.path);
            const imageUrl = await getDownloadURL(imageRef);
            setBusinessImage(imageUrl);
            // Use businessImage as logo if no separate logo exists
            if (!businessData.logo) {
              setBusinessLogo(imageUrl);
            }
          } catch (error) {
            console.log('Error getting business image:', error);
          }
        }
        
        if (businessData.logo && businessData.logo.path) {
          try {
            const logoRef = ref(storage, businessData.logo.path);
            const logoUrl = await getDownloadURL(logoRef);
            setBusinessLogo(logoUrl);
          } catch (error) {
            console.log('Error getting business logo:', error);
          }
        }
        
        // Format business data
        setBusiness({
          id: businessId,
          name: businessData.name || 'Unknown Business',
          description: businessData.about || businessData.description || 'No description available',
          website: businessData.website || null,
          email: businessData.contactInfo?.email || businessData.email || null,
          phone: businessData.contactInfo?.phone || businessData.phone || businessData.phoneNumber || null,
          address: (() => {
            const addr = businessData.address;
            if (!addr) return 'Address not specified';
            
            const addressParts = [];
            if (addr.street && addr.street.trim() && addr.street !== '0') {
              addressParts.push(addr.street.trim());
            }
            if (addr.city && addr.city.trim() && addr.city !== '0') {
              addressParts.push(addr.city.trim());
            }
            if (addr.state && addr.state.trim() && addr.state !== '0') {
              addressParts.push(addr.state.trim());
            }
            if (addr.zipcode && addr.zipcode.trim() && addr.zipcode !== '0') {
              addressParts.push(addr.zipcode.trim());
            }
            return addressParts.length > 0 ? addressParts.join(', ') : 'Address not specified';
          })(),
          city: businessData.address?.city || 'Unknown Location',
          state: businessData.address?.state || null,
          zipcode: businessData.address?.zipcode || null,
          coordinates: businessData.address?.coordinates || null,
          type: businessData.type || 'Not specified',
          yearFounded: businessData.yearFounded || null,
          employeeScale: businessData.employeeScale || 'Not specified',
          socialMedia: businessData.socialMedia || {},
          taxId: businessData.taxId || null,
          managerUserId: businessData.managerUserId || null
        });
        
        // Fetch jobs from this business
        const jobsQuery = query(
          collection(firestore, 'jobs'),
          where('businessId', '==', businessId),
          limit(10)
        );
        
        const jobsSnapshot = await getDocs(jobsQuery);
        console.log(`Found ${jobsSnapshot.size} jobs for this business`);
        
        if (!jobsSnapshot.empty) {
          const jobsData = jobsSnapshot.docs.map(doc => {
            const jobData = doc.data();
            
            // Calculate posted days
            const postedDate = jobData.createdAt?.__time__ 
              ? new Date(jobData.createdAt.__time__) 
              : jobData.createdAt?.toDate?.() || new Date();
              
            const currentDate = new Date();
            const diffTime = currentDate - postedDate;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            // Format salary
            let formattedSalary = 'Salary not specified';
            if (jobData.hourlyRate) {
              const hourlyRate = typeof jobData.hourlyRate === 'number' 
                  ? jobData.hourlyRate 
                  : parseFloat(jobData.hourlyRate);
              
              if (!isNaN(hourlyRate)) {
                if (hourlyRate > 1000) {
                  formattedSalary = `$${(hourlyRate/1000).toFixed(0)}k/year`;
                } else {
                  formattedSalary = `$${hourlyRate.toFixed(2)}/hr`;
                }
              }
            } else if (jobData.salary) {
              formattedSalary = jobData.salary;
            }
            
            return {
              id: doc.id,
              title: jobData.title || 'Untitled Position',
              description: jobData.description || 'No description available',
              location: jobData.address?.city || businessData.address?.city || 'Unknown Location',
              salary: formattedSalary,
              jobType: jobData.jobType || 'Not specified',
              postedDays: diffDays,
              isActive: jobData.isActive !== false,
              immediateStart: jobData.isImmediateStart
            };
          });
          
          setBusinessJobs(jobsData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching business details:', error);
        setLoading(false);
      }
    };
    
    fetchBusinessDetails();
  }, [businessId]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }
  
  if (!business) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-xl shadow-sm p-10 max-w-2xl w-full text-center">
          <div className="bg-red-100 p-4 rounded-full inline-block mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3 text-left">Business Not Found</h1>
          <p className="text-gray-600 mb-6 text-left">
            The business you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/dashboard/businesses')}
            className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors"
          >
            Browse All Businesses
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-700 hover:text-[#FF8C00] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span>Back to Businesses</span>
          </button>
        </div>
        
        {/* Business header section */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
          {/* Cover image */}
          <div className="h-48 bg-gray-200 relative">
            {businessImage ? (
              <img src={businessImage} alt={business.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 22V12h6v10" />
                </svg>
              </div>
            )}
          </div>
          
          {/* Business info */}
          <div className="px-8 pt-6 pb-8 relative">
            {/* Logo (positioned to overlap the cover image) */}
            <div className="absolute -top-14 left-8 w-24 h-24 bg-white rounded-xl shadow p-1.5">
              <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {businessLogo ? (
                  <img src={businessLogo} alt={`${business.name} logo`} className="w-full h-full object-contain" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
            </div>
            
            {/* Business name and type */}
            <div className="ml-28">
              <h1 className="text-3xl font-bold text-gray-800 text-left">{business.name}</h1>
              <p className="text-gray-600 text-left">{business.type}</p>
            </div>
            
            {/* Business details grid */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column - About */}
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">About</h2>
                <p className="text-gray-700 whitespace-pre-line text-left">{business.description}</p>
                
                {/* Contact information */}
                <div className="mt-6 space-y-2">
                  {business.address && business.address !== 'Address not specified' && (
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-700 text-left">{business.address}</span>
                    </div>
                  )}
                  
                  {business.website && business.website.trim() && business.website !== '0' && (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline text-left">
                        {business.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    </div>
                  )}
                  
                  {business.email && business.email.trim() && business.email !== '0' && (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${business.email}`} className="text-[#FF8C00] hover:underline text-left">{business.email}</a>
                    </div>
                  )}
                  
                  {business.phone && business.phone.trim() && business.phone !== '0' && (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <a href={`tel:${business.phone}`} className="text-[#FF8C00] hover:underline text-left">{business.phone}</a>
                    </div>
                  )}
                </div>
                
                {/* Social media links */}
                {business.socialMedia && Object.keys(business.socialMedia).length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-2 text-left">Connect with us</h3>
                    <div className="flex space-x-3">
                      {business.socialMedia.facebook && (
                        <a href={business.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                          </svg>
                        </a>
                      )}
                      {business.socialMedia.twitter && (
                        <a href={business.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                          </svg>
                        </a>
                      )}
                      {business.socialMedia.linkedin && (
                        <a href={business.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-900">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
                          </svg>
                        </a>
                      )}
                      {business.socialMedia.instagram && (
                        <a href={business.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-800">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right column - Business details */}
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4 text-left">Company Details</h2>
                <div className="bg-gray-50 rounded-lg p-5 space-y-4">
                  {/* Founded year - enhanced handling */}
                  <div className="flex items-center">
                    <div className="w-8 flex-shrink-0 text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 text-left">Founded</h3>
                      <p className="text-sm text-gray-700 text-left">
                        {(() => {
                          const year = business.yearFounded;
                          if (year && 
                              year !== 0 && 
                              year !== '0' && 
                              String(year).trim() !== '0' &&
                              parseInt(year) > 0 &&
                              !isNaN(year)) {
                            return year;
                          }
                          return 'unknown';
                        })()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Employee count */}
                  <div className="flex items-center">
                    <div className="w-8 flex-shrink-0 text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 914 0zM7 10a2 2 0 11-4 0 2 2 0 914 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 text-left">Company Size</h3>
                      <p className="text-sm text-gray-700 text-left">{business.employeeScale}</p>
                    </div>
                  </div>
                  
                  {/* Industry / Type */}
                  <div className="flex items-center">
                    <div className="w-8 flex-shrink-0 text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 text-left">Industry</h3>
                      <p className="text-sm text-gray-700 text-left">{business.type}</p>
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="flex items-center">
                    <div className="w-8 flex-shrink-0 text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 text-left">Location</h3>
                      <p className="text-sm text-gray-700 text-left">{business.city}{business.state ? `, ${business.state}` : ''}</p>
                    </div>
                  </div>

                  {/* Tax ID (if available and not "Not provided") */}
                  {business.taxId && business.taxId !== 'Not provided' && business.taxId.trim() && business.taxId !== '0' && (
                    <div className="flex items-center">
                      <div className="w-8 flex-shrink-0 text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 text-left">Tax ID</h3>
                        <p className="text-sm text-gray-700 text-left">{business.taxId}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Jobs section */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 text-left">Jobs at {business.name}</h2>
            <button 
              onClick={() => navigate(`/dashboard/jobs?query=${encodeURIComponent(business.name)}`)}
              className="px-4 py-2 text-sm bg-[#FF8C00] text-white rounded hover:bg-[#FF8C00]/90 transition-colors"
            >
              View All Jobs
            </button>
          </div>
          
          {businessJobs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {businessJobs.map(job => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <a 
                    href={`/dashboard/jobs/${job.id}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <h3 className="text-lg font-medium text-gray-900 hover:text-[#FF8C00] mb-2 truncate text-left">{job.title}</h3>
                    <div className="flex flex-wrap items-center text-sm text-gray-600 gap-x-3 mb-3 text-left">
                      <span>{job.location}</span>
                      <span>•</span>
                      <span>{job.jobType}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-3 line-clamp-2 text-left">{job.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-[#FF8C00] text-left">{job.salary}</span>
                      <span className="text-xs text-gray-500">{job.postedDays} {job.postedDays === 1 ? 'day' : 'days'} ago</span>
                    </div>
                    {!job.isActive && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          No longer available
                        </span>
                      </div>
                    )}
                    {job.immediateStart && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Immediate Start
                        </span>
                      </div>
                    )}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-800 mb-2 text-left">No jobs available</h3>
              <p className="text-gray-600 mb-4 text-left">This business doesn't have any job openings at the moment.</p>
              <button 
                onClick={() => navigate('/dashboard/jobs')}
                className="px-4 py-2 text-sm bg-[#FF8C00] text-white rounded hover:bg-[#FF8C00]/90 transition-colors"
              >
                Browse All Jobs
              </button>
            </div>
          )}
        </div>
        
        {/* Similar businesses section (optional) */}
        {/* Can be added later to show related businesses in the same industry or location */}
      </div>
    </div>
  );
};

export default BusinessDetailPage; 